import { VALIDATION, PERFORMANCE } from './constants';
import type { ResolutionOption } from '@/types';

/**
 * Format currency values
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Format date values
 */
export const formatDate = (date: string | Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(date));
};

export const formatRelativeDate = (date: string | Date): string => {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInMs = now.getTime() - targetDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

/**
 * Format file size
 */
export const formatFileSize = (bytes: number): string => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Generate unique ID
 */
export const generateId = (): string => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

/**
 * Debounce function
 */
export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number = PERFORMANCE.DEBOUNCE_DELAY
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout;

  return (...args: Parameters<T>): void => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), wait);
  };
};

/**
 * Throttle function
 */
export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle = false;

  return (...args: Parameters<T>): void => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Deep clone object
 */
export const deepClone = <T>(obj: T): T => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
  if (typeof obj === 'object') {
    const cloned = {} as T;
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = deepClone(obj[key]);
      }
    }
    return cloned;
  }
  return obj;
};

/**
 * Check if value is empty
 */
export const isEmpty = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Validation helpers
 */
export const isValidEmail = (email: string): boolean => {
  return VALIDATION.EMAIL_PATTERN.test(email);
};

export const isValidPassword = (password: string): boolean => {
  return password.length >= VALIDATION.PASSWORD_MIN_LENGTH;
};

export const isValidPhoneNumber = (phone: string): boolean => {
  return VALIDATION.PHONE_PATTERN.test(phone);
};

/**
 * URL helpers
 */
export const buildUrl = (
  base: string,
  path: string,
  params?: Record<string, string | number>
): string => {
  const url = new URL(path, base);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });
  }
  return url.toString();
};

export const getImageUrl = (path: string, size?: 'thumbnail' | 'web' | 'full'): string => {
  if (!path) return '/placeholder-image.jpg';
  if (path.startsWith('http')) return path;

  const baseUrl = import.meta.env.VITE_CDN_URL || '';
  const sizePrefix = size ? `/${size}` : '';
  return `${baseUrl}${sizePrefix}${path}`;
};

/**
 * Array helpers
 */
export const groupBy = <T, K extends keyof T>(array: T[], key: K): Record<string, T[]> => {
  return array.reduce(
    (groups, item) => {
      const groupKey = String(item[key]);
      groups[groupKey] = groups[groupKey] || [];
      groups[groupKey].push(item);
      return groups;
    },
    {} as Record<string, T[]>
  );
};

export const sortBy = <T>(array: T[], key: keyof T, direction: 'asc' | 'desc' = 'asc'): T[] => {
  return [...array].sort((a, b) => {
    const valueA = a[key];
    const valueB = b[key];

    if (valueA === valueB) return 0;

    const comparison = valueA < valueB ? -1 : 1;
    return direction === 'asc' ? comparison : -comparison;
  });
};

export const removeDuplicates = <T>(array: T[], key?: keyof T): T[] => {
  if (!key) return [...new Set(array)];

  const seen = new Set();
  return array.filter(item => {
    const value = item[key];
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

/**
 * Grid layout helpers
 */
export const calculateGridColumns = (
  containerWidth: number,
  minItemWidth: number,
  gap: number
): number => {
  return Math.max(1, Math.floor((containerWidth + gap) / (minItemWidth + gap)));
};

export const calculateItemWidth = (
  containerWidth: number,
  columns: number,
  gap: number
): number => {
  return Math.floor((containerWidth - gap * (columns - 1)) / columns);
};

/**
 * Local storage helpers
 */
export const getStorageItem = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const setStorageItem = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Error handled via UI state
  }
};

export const removeStorageItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Error handled via UI state
  }
};

/**
 * Error handling helpers
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unexpected error occurred';
};

export const isNetworkError = (error: unknown): boolean => {
  return (
    error instanceof Error && (error.message.includes('Network') || error.message.includes('fetch'))
  );
};

/**
 * Resolution helpers
 */
export const getResolutionPrice = (basePrice: number, resolution: ResolutionOption): number => {
  return Math.max(0, basePrice + resolution.price);
};

export const getResolutionMultiplier = (resolution: string): number => {
  switch (resolution) {
    case 'web':
      return 1;
    case 'print':
      return 3;
    case 'full':
      return 5;
    default:
      return 1;
  }
};

/**
 * Cart helpers
 */
export const calculateDiscount = (
  itemCount: number,
  subtotal: number
): { amount: number; percentage: number; tier: string } => {
  if (itemCount >= 30) {
    return { amount: subtotal * 0.2, percentage: 20, tier: 'large' };
  } else if (itemCount >= 15) {
    return { amount: subtotal * 0.15, percentage: 15, tier: 'medium' };
  } else if (itemCount >= 5) {
    return { amount: subtotal * 0.1, percentage: 10, tier: 'small' };
  }
  return { amount: 0, percentage: 0, tier: 'none' };
};

/**
 * SEO helpers
 */
export const generateMetaDescription = (eventTitle?: string, photoCount?: number): string => {
  if (eventTitle && photoCount) {
    return `View and purchase professional photos from ${eventTitle}. ${photoCount} high-quality photos available for download.`;
  }
  return 'Professional photo sharing and sales platform for photographers and clients.';
};

export const generatePageTitle = (title?: string): string => {
  const baseTitle = 'Pixel Share';
  return title ? `${title} | ${baseTitle}` : baseTitle;
};

/**
 * Responsive helpers
 */
export const getBreakpointValue = (
  breakpoint: keyof typeof import('./constants').BREAKPOINTS
): number => {
  const BREAKPOINTS = {
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536,
  } as const;
  return BREAKPOINTS[breakpoint];
};

export const isMobile = (): boolean => {
  return window.innerWidth < getBreakpointValue('md');
};

export const isTablet = (): boolean => {
  const width = window.innerWidth;
  return width >= getBreakpointValue('md') && width < getBreakpointValue('lg');
};

export const isDesktop = (): boolean => {
  return window.innerWidth >= getBreakpointValue('lg');
};
